import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient } from '@angular/common/http';
import { AuthenticationInterceptorService } from './core/authentication-interceptor.service';
import { SplashComponent } from './framework/splash/splash.component';
import { AuthorizationModule } from './authorization/authorization.module';
import { Router } from '@angular/router';
import { SharedModule } from './shared/shared.module';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { AppStoreModule } from './store/app-store.module';
import { OrganizationService } from './development/organization.service';
import { ToastrModule } from 'ngx-toastr';
import { SharedService } from './development/shared.service';
import { LetDirective, PushPipe } from '@ngrx/component';
import { UiKitModule } from './shared/ui-kit/ui-kit.module';
import { AlertEventsService } from './development/alert-events.service';
import { OrganizationLayoutComponent } from './pages/organization-layout/organization-layout.component';
import { AppSettingsModule } from './settings/app-settings.module';
import { SearchConfigurationService } from './development/search-configuration.service';
import { AlertMonitoringService } from './development/alert-monitoring.service';
import { CameraService } from './development/camera.service';
import { CameraSearchSettingsService } from './development/camera-search-settings.service';
import { NoAuthComponent } from './pages/no-auth/no-auth.component';
import { GrantedAccessComponent } from './pages/granted-access/granted-access.component';
import { GrantedAccessService } from './development/granted-access.service';
import { StatsService } from './development/stats.service';
import { UserSettingsService } from './development/user-settings.service';
import { ResizableModule } from 'angular-resizable-element';
import { ActivityLogService } from './development/activity-log.service';
import { VariableService } from './development/variable.service';
import { TermsComponent } from './eula/terms/terms.component';
import { PrivacyComponent } from './eula/privacy/privacy.component';
import { SearchComponent } from './pages/search/search.component';
import { SavedSearchService } from './development/saved-search.service';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { AppConfig } from './services/app.config';
import { PipesModule } from './pipes/pipes.module';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { CommentService } from './services/comment.service';
import { WebrtcDebugDialogComponent } from './framework/webrtc-player/webrtc-debug-dialog/webrtc-debug-dialog.component';
import { UtilsV2Service } from './services/utils-v2.service';
import { PeopleService } from './development/people.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MatOption, MatRippleModule } from '@angular/material/core';
import { DeveloperGuard } from './guards/developer.guard';
import { LocationEditService } from './development/location-edit.service';
import { LocationComponent } from './pages/location/location.component';
import { FullPageComponent } from './layout/full-page/full-page.component';
import * as Sentry from '@sentry/angular';
import { SharedAlertComponent } from './pages/granted-access/components/shared-alert/shared-alert.component';
import { SharedArchiveComponent } from './pages/granted-access/components/shared-archive/shared-archive.component';
import { InviteUsersModalComponent } from '@modals/invite-users-modal/invite-users-modal.component';
import { PendingInvitationsModalComponent } from '@modals/pending-invitations-modal/pending-invitations-modal.component';
import { DoorsService } from './development/doors.service';
import { CustomEventsService } from './development/custom-events.service';
import { UserGroupsServices } from './services/user-groups.services';
import { VehiclesService } from './development/vehicles.service';
import { UniformsService } from './development/uniforms.service';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { PermissionGroupService } from './services/permission-group.service';
import { UsersListModalComponent } from '@modals/user-list-modal/users-list-modal.component';
import { OrgGroupsService } from './development/org-groups.service';
import { CameraEditGuard } from './guards/camera-edit.guard';
import { RouterPermissionGuard } from './guards/router-permission-guard';
import { CameraCreateGuard } from './guards/camera-create.guard';
import { DashboardService } from './development/dashboard.service';
import { UiDropdownComponent } from './shared/ui-kit/ui-dropdown/ui-dropdown.component';
import { RbacOrgAllowDirective } from './authorization/rbac-org-allow.directive';
import { PermissionService } from './services/permission.service';
import { WidgetService } from './development/widget.service';
import { LockService } from './development/lock.service';
import { BrowserCompatibilityModalComponent } from '@modals/browser-compatibility-modal/browser-compatibility-modal.component';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { IdentityService } from './services/identity.service';
import { LivekitService } from './development/livekit.service';
import { SupportService } from './services/support.service';
import { UserDeviceService } from './services/user-device.service';
import { MfaService } from './services/mfa.service';
import { WebhookService } from './services/webhook.service';
import { FloorService } from './development/floor.service';
import { WallV2Service } from './services/wall-v2.service';
import { YoutubeService } from './services/youtube.service';
import { AccessDoorsService } from './services/access-doors.service';
import { CamerasSettingsService } from './services/cameras-settings.service';
import { BreadcrumbsService } from './services/breadcrumbs.service';
import { SharedApiService } from './services/shared-api.service';
import { AlarmService } from './services/alarm.service';
import { ScheduleService } from './services/schedule.service';
import { SharedWallComponent } from './pages/granted-access/components/shared-wall/shared-wall.component';
import { EmailVerificationComponent } from './authentication/email-verification/email-verification.component';
import { SharedLiveviewComponent } from './pages/granted-access/components/shared-liveview/shared-liveview.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeHe from '@angular/common/locales/he';

import { CameraPickerInlineComponent } from '@modals/camera-picker-inline/camera-picker-inline.component';
import { Directionality } from '@angular/cdk/bidi';
import { CustomDirectionality } from './services/directionality.service';
import { IfDeveloperDirective } from './directives/if-developer.directive';
import {
  UiHeaderWithBreadcrumbsComponent,
} from './shared/ui-kit/ui-header-with-breadcrumbs/ui-header-with-breadcrumbs.component';
import { TranslateAndSlicePipe } from './pipes/translate-and-slice.pipe';
import { TranslationService } from './services/translation.service';
import {
  MatAccordion, MatExpansionModule,
  MatExpansionPanel,
  MatExpansionPanelDescription, MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatDialogClose } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';

export const initializeApp1 = (appInitService: AppConfig): () => Promise<any> => {
  return (): Promise<any> => appInitService.load();
};

export function createTranslateLoader(http: any) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

registerLocaleData(localeEn);
registerLocaleData(localeHe);

@NgModule({
  declarations: [
    AppComponent,
    SplashComponent,
    OrganizationLayoutComponent,
    NoAuthComponent,
    GrantedAccessComponent,
    TermsComponent,
    PrivacyComponent,
    SearchComponent,
    NotFoundComponent,
    WebrtcDebugDialogComponent,
    LocationComponent,
    FullPageComponent,
    SharedAlertComponent,
    SharedArchiveComponent,
    SharedLiveviewComponent,
    InviteUsersModalComponent,
    PendingInvitationsModalComponent,
    UsersListModalComponent,
    BrowserCompatibilityModalComponent,
    EmailVerificationComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule, // routings available
    AuthorizationModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    SharedModule,
    AppStoreModule,
    StoreRouterConnectingModule.forRoot(),
    // StoreRouterConnectingModule.forRoot({
    //   stateKey: 'router',
    //   routerState: RouterState.Minimal,
    // }),
    LazyLoadImageModule,
    LetDirective,
    PushPipe,
    UiKitModule,
    MonacoEditorModule.forRoot(),
    AppSettingsModule,
    ResizableModule,
    PipesModule,
    NgScrollbarModule,
    ServiceWorkerModule.register('ngsw-custom-worker.js', {
      enabled: true,
      //   enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatRippleModule,
    UiDropdownComponent,
    RbacOrgAllowDirective,
    SharedWallComponent,
    TranslatePipe,
    CameraPickerInlineComponent,
    IfDeveloperDirective,
    UiHeaderWithBreadcrumbsComponent,
    TranslateAndSlicePipe,
    MatExpansionModule,
    MatAccordion,
    MatExpansionPanelDescription,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatIcon,
    MatTooltip,
    MatSlider,
    MatSliderThumb,
    ScrollingModule,
    MatDialogClose,
    MatLabel,
    MatSelect,
    MatOption,
    MatCheckbox,
  ],
  providers: [
    AppConfig,
    { provide: APP_INITIALIZER, useFactory: initializeApp1, deps: [AppConfig], multi: true },
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => translateService.currentLang || 'en-US',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptorService,
      multi: true,
    },
    { provide: Window, useValue: window },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        // showDialog: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideHttpClient(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }).providers!,
    { provide: Directionality, useExisting: CustomDirectionality },
    OrganizationService,
    SharedService,
    AlertEventsService,
    VariableService,
    SavedSearchService,
    SearchConfigurationService,
    AlertMonitoringService,
    CameraService,
    CameraSearchSettingsService,
    GrantedAccessService,
    LocationEditService,
    StatsService,
    UserSettingsService,
    ActivityLogService,
    CommentService,
    UtilsV2Service,
    PeopleService,
    DeveloperGuard,
    DoorsService,
    VehiclesService,
    UniformsService,
    CustomEventsService,
    UserGroupsServices,
    OrgGroupsService,
    PermissionGroupService,
    CameraEditGuard,
    RouterPermissionGuard,
    CameraCreateGuard,
    DashboardService,
    PermissionService,
    WidgetService,
    LockService,
    IdentityService,
    LivekitService,
    SupportService,
    UserDeviceService,
    MfaService,
    WebhookService,
    FloorService,
    WallV2Service,
    YoutubeService,
    SharedApiService,
    AccessDoorsService,
    CamerasSettingsService,
    BreadcrumbsService,
    AlarmService,
    ScheduleService,
  ],
  bootstrap: [AppComponent],
  exports: [LocationComponent],
})
export class AppModule {
  constructor(trace: Sentry.TraceService, public translateService: TranslationService) {
    this.translateService.initLanguage();
  }
}
